import {
  Modal as HeroUIMModal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalProps as HeroUIModalProps,
  Button,
} from "@heroui/react";
import { DotsThreeVerticalIcon, IconProps } from "@phosphor-icons/react";
import { cloneElement, ReactElement } from "react";
import { useOpenState } from "@/shared/hooks/useOpenState";

interface ModalProps extends HeroUIModalProps {
  actionsDisabled?: boolean;
  icon?: ReactElement<IconProps>;
  title?: string;
  id: string;
  triggerProps?: {
    isDisabled?: boolean;
  };
  closeLabel?: string;
  confirmLabel?: string;
  onConfirm?: () => void;
  externalState?: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  };
}

export function Modal({
  actionsDisabled,
  children,
  icon,
  id,
  triggerProps,
  title,
  closeLabel,
  confirmLabel,
  onConfirm,
  externalState,
  ...rest
}: ModalProps) {
  const { isOpen, handleOpenChange } = useOpenState({ id, externalState });
  const iconElement = icon ? (
    cloneElement(icon, { size: 24, ...icon?.props })
  ) : (
    <DotsThreeVerticalIcon size={24} weight="bold" />
  );

  return (
    <>
      {externalState ? null : (
        <Button
          isDisabled={triggerProps?.isDisabled}
          isIconOnly
          size="sm"
          variant="light"
          onPress={() => handleOpenChange(!isOpen)}
        >
          {iconElement}
        </Button>
      )}
      <HeroUIMModal
        className="mx-4"
        hideCloseButton
        isOpen={isOpen}
        placement="center"
        onOpenChange={handleOpenChange}
        {...rest}
      >
        <ModalContent>
          {() => (
            <>
              {title && <ModalHeader>{title}</ModalHeader>}
              <ModalBody>{children}</ModalBody>
              {closeLabel || onConfirm ? (
                <ModalFooter>
                  {closeLabel ? (
                    <Button
                      isDisabled={actionsDisabled}
                      color={onConfirm ? "default" : "primary"}
                      variant={onConfirm ? "flat" : "solid"}
                      onPress={() => handleOpenChange(false)}
                    >
                      {closeLabel}
                    </Button>
                  ) : null}
                  {onConfirm && (
                    <Button color="primary" isDisabled={actionsDisabled} onPress={onConfirm}>
                      {confirmLabel}
                    </Button>
                  )}
                </ModalFooter>
              ) : null}
            </>
          )}
        </ModalContent>
      </HeroUIMModal>
    </>
  );
}
